import { z } from 'zod'

export enum exam_analysis_category {
  GESTANTE = 'GESTANTE',
  TENTANTE = 'TENTANTE',
  MULHER = 'MULHER',
  CRIANCA = 'CRIANCA',
  HOMEM = 'HOMEM'
}

export const formatted_exam_analysis_category: Record<exam_analysis_category, string> = {
  [exam_analysis_category.GESTANTE]: 'Gestante',
  [exam_analysis_category.TENTANTE]: 'Tentante',
  [exam_analysis_category.MULHER]: 'Mulher',
  [exam_analysis_category.CRIANCA]: 'Criança',
  [exam_analysis_category.HOMEM]: 'Homem'
}

export enum exam_analysis_trimester {
  TRIM1 = 'TRIM1',
  TRIM2 = 'TRIM2',
  TRIM3 = 'TRIM3'
}

export const formatted_exam_analysis_trimester: Record<exam_analysis_trimester, string> = {
  [exam_analysis_trimester.TRIM1]: '1º Trimestre',
  [exam_analysis_trimester.TRIM2]: '2º Trimestre',
  [exam_analysis_trimester.TRIM3]: '3º Trimestre'
}

export enum exam_analysis_children_range {
  R_0_6M = 'R_0_6M',
  R_6M_2A = 'R_6M_2A',
  R_2_10A = 'R_2_10A',
  R_10_18A = 'R_10_18A'
}

export const formatted_exam_analysis_children_range: Record<exam_analysis_children_range, string> = {
  [exam_analysis_children_range.R_0_6M]: '0 a 6 meses',
  [exam_analysis_children_range.R_6M_2A]: '6 meses a 2 anos',
  [exam_analysis_children_range.R_2_10A]: '2 a 10 anos',
  [exam_analysis_children_range.R_10_18A]: '10 a 18 anos'
}

export enum marker_status {
  EMPTY = 'EMPTY',
  OK = 'OK',
  ABOVE = 'ABOVE',
  BELOW = 'BELOW',
  ATTENTION = 'ATTENTION'
}

/**
 * Cada coleta é uma **coluna** na tabela de marcadores.
 * `date` em ISO `yyyy-MM-dd` (ou null se ainda não informada).
 * A última coleta da lista é considerada a "atual".
 */
export interface ExamCollection {
  ref: string
  date?: string | null
}

/**
 * Valores preenchidos: indexados primeiro por id do marcador, depois pelo
 * `ref` da coleta. Mantemos como string para preservar formato (vírgula),
 * validando numericamente apenas na classificação.
 */
export type ExamMarkerValues = Record<string, Record<string, string>>

export enum exam_analysis_status {
  DRAFT = 'DRAFT',
  PROCESSING = 'PROCESSING',
  ACTIVE = 'ACTIVE',
  FAILED = 'FAILED',
  DELETED = 'DELETED'
}

export const formatted_exam_analysis_status: Record<exam_analysis_status, string> = {
  [exam_analysis_status.DRAFT]: 'Rascunho',
  [exam_analysis_status.PROCESSING]: 'Processando',
  [exam_analysis_status.ACTIVE]: 'Concluída',
  [exam_analysis_status.FAILED]: 'Falhou',
  [exam_analysis_status.DELETED]: 'Deletado'
}

export interface ExamAnalysisProps {
  id?: number
  ref: string
  userId?: number
  patientName: string
  category: exam_analysis_category
  trimester?: exam_analysis_trimester | null
  childrenRange?: exam_analysis_children_range | null
  /** Idade gestacional (gestante) — informada manualmente. */
  gestationalWeeks?: number | null
  gestationalDays?: number | null
  collections: ExamCollection[]
  markerValues: ExamMarkerValues
  title?: string | null
  summary?: string | null
  response?: string | null
  contentHtml?: string | null
  spentValue?: number | null
  status: exam_analysis_status
  createdAt: string
  updatedAt?: string | null
}

export const examAnalysisFormSchema = z.object({
  patientName: z.string().min(2, 'Nome inválido.').max(255, 'Limite 255 caracteres.'),
  category: z.nativeEnum(exam_analysis_category, { errorMap: () => ({ message: 'Categoria inválida.' }) }),
  trimester: z.nativeEnum(exam_analysis_trimester).optional().nullable(),
  childrenRange: z.nativeEnum(exam_analysis_children_range).optional().nullable(),
  gestationalWeeks: z.coerce.number().int().min(0, 'Mínimo 0.').max(42, 'Máximo 42 semanas.').optional().nullable(),
  gestationalDays: z.coerce.number().int().min(0, 'Mínimo 0.').max(6, 'Máximo 6 dias.').optional().nullable()
})

export type ExamAnalysisFormSchema = z.infer<typeof examAnalysisFormSchema>

export function validateExamAnalysisCrossFields(values: ExamAnalysisFormSchema): string | null {
  if (values.category === exam_analysis_category.GESTANTE && !values.trimester) return 'Informe o trimestre.'
  if (values.category === exam_analysis_category.CRIANCA && !values.childrenRange) return 'Informe a faixa etária.'
  return null
}

/**
 * Conta IDs de marcadores que têm valor preenchido em AO MENOS uma coleta.
 */
export const countFilledMarkers = (record: Pick<ExamAnalysisProps, 'markerValues'>): number => {
  let n = 0
  const map = record.markerValues || {}
  for (const k of Object.keys(map)) {
    const byCol = map[k] || {}
    if (Object.values(byCol).some(v => v !== undefined && v !== null && String(v).trim() !== '')) n++
  }
  return n
}

/**
 * Conta valores preenchidos numa coleta específica (independente de marcador).
 */
export const countFilledInCollection = (record: Pick<ExamAnalysisProps, 'markerValues'>, collectionRef: string): number => {
  let n = 0
  const map = record.markerValues || {}
  for (const k of Object.keys(map)) {
    const v = map[k]?.[collectionRef]
    if (v !== undefined && v !== null && String(v).trim() !== '') n++
  }
  return n
}

export const formatContext = (record: Pick<ExamAnalysisProps, 'category' | 'trimester' | 'childrenRange'>): string => {
  if (record.category === exam_analysis_category.GESTANTE && record.trimester) {
    return formatted_exam_analysis_trimester[record.trimester]
  }
  if (record.category === exam_analysis_category.CRIANCA && record.childrenRange) {
    return formatted_exam_analysis_children_range[record.childrenRange]
  }
  return '—'
}

/**
 * Trimestre inferido a partir do número de semanas informado.
 * Convenção Mãe Nutrida: 1-14 → 1º; 15-27 → 2º; 28-42 → 3º.
 */
export function trimesterFromWeeks(weeks?: number | null): exam_analysis_trimester | null {
  if (weeks === undefined || weeks === null || Number.isNaN(weeks) || weeks <= 0) return null
  if (weeks <= 14) return exam_analysis_trimester.TRIM1
  if (weeks <= 27) return exam_analysis_trimester.TRIM2
  return exam_analysis_trimester.TRIM3
}
